// src/context/AuthContext.js
// SECURITY: Proper logout with back-button prevention
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

const AuthContext = createContext(null);

// ── Token validation ─────────────────────────────────────────
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    // exp is in seconds, Date.now() is ms
    return payload.exp * 1000 < Date.now();
  } catch {
    return true; // invalid token = treat as expired
  }
}

function clearSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  // Set a flag so ProtectedRoute knows the session was explicitly ended
  sessionStorage.setItem("logged_out", "1");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: check stored session is still valid
  useEffect(() => {
    const token = localStorage.getItem("token");
    const stored = localStorage.getItem("user");
    const loggedOut = sessionStorage.getItem("logged_out");

    if (loggedOut) {
      // User explicitly logged out in this browser tab/session
      // Don't restore session even if localStorage still has data
      clearSession();
      setLoading(false);
      return;
    }

    if (token && stored) {
      if (isTokenExpired(token)) {
        // Token expired — clear everything silently
        clearSession();
        setUser(null);
      } else {
        setUser(JSON.parse(stored));
      }
    }
    setLoading(false);
  }, []);

  // Periodically check token expiry (every 60 seconds)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token || isTokenExpired(token)) {
        clearSession();
        setUser(null);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback((userData, token) => {
    // Clear any previous logout flag when logging in fresh
    sessionStorage.removeItem("logged_out");
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, login, logout, loading, isTokenExpired }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
